import { useState, useEffect } from "react";
import { SectionList, Pressable, View, Linking, Text, ActivityIndicator, Image, LayoutAnimation, UIManager, Platform } from "react-native";
import { FilterIcon, ChevronUpIcon, ChevronDownIcon } from "@/components/Icons";

if (Platform.OS === "android" && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}
import { MatchesSkeleton } from "@/components/Skeleton";
import styled from "styled-components/native";
import { useRouter } from "expo-router";
import { collection, onSnapshot, doc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { useMatches } from "@/hooks/useMatches";
import { useTrainings } from "@/hooks/useTrainings";
import { useMyAvailability, useMyTrainingAttendance } from "@/hooks/useMyAvailability";
import { setDayAvailability, setTrainingAttendance } from "@/lib/services";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import type { Match, Training, AvailabilityStatus } from "@/lib/types";

function isAvailabilityOpen(opensAt?: string): boolean {
  if (!opensAt) return true;
  return opensAt <= new Date().toISOString().slice(0, 10);
}

function formatOpensAt(opensAt: string): string {
  try {
    const d = new Date(opensAt + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch { return opensAt; }
}

function formatDayHeader(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  } catch { return dateStr; }
}

function formatTime(timeStr: string): string {
  try {
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    if (hour >= 12) return `${hour === 12 ? 12 : hour - 12}:${m}pm`;
    return `${hour === 0 ? 12 : hour}:${m}am`;
  } catch { return timeStr; }
}

const statusColors: Record<AvailabilityStatus, { bg: string; border: string; text: string; label: string }> = {
  available: { bg: colors.brand50, border: colors.brandLight, text: colors.brandDark, label: "Available" },
  unavailable: { bg: colors.dangerLight, border: "#fecaca", text: colors.danger, label: "Unavailable" },
  maybe: { bg: colors.amber50, border: colors.amber200, text: colors.amber, label: "Maybe" },
};

// ── Styled Components ──────────────────────────────────

const Header = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 12px;
`;

const HeaderLabel = styled.Text`
  font-size: 12px;
  font-weight: 800;
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const NewBtn = styled.Pressable`
  background-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding: 10px 16px;
`;

const NewBtnText = styled.Text`
  color: #fff;
  font-size: 12px;
  font-weight: 700;
`;

const DayCardShadow = styled.View`
  margin: 8px 20px 20px;
  border-radius: ${radius.lg}px;
  background-color: ${colors.white};
  shadow-color: #000;
  shadow-offset: 0px 4px;
  shadow-opacity: 0.12;
  shadow-radius: 12px;
  elevation: 4;
`;

const DayCard = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  overflow: hidden;
`;

const DayHeader = styled.View`
  padding: 14px 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
  background-color: ${colors.bg};
`;

const DayTitle = styled.Text`
  font-size: 15px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
`;

const DayMatchCount = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const AvailRow = styled.View`
  flex-direction: row;
  gap: 8px;
  padding: 12px 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
`;

const MatchRow = styled.Pressable`
  flex-direction: row;
  align-items: center;
  padding: 12px 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
`;

const MatchInfo = styled.View`
  flex: 1;
`;

const MatchTeam = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const MatchMeta = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin-top: 2px;
`;

const MatchStatus = styled.View<{ bg: string; border: string }>`
  border-radius: 999px;
  padding: 3px 8px;
  background-color: ${(p) => p.bg};
  border: 1px solid ${(p) => p.border};
`;

const MatchStatusText = styled.Text<{ color: string }>`
  font-size: 10px;
  font-weight: 700;
  color: ${(p) => p.color};
`;

const EmptyCard = styled.View`
  margin: 0 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 32px;
  align-items: center;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
`;

const Spacer = styled.View<{ h?: number }>`
  height: ${(p) => p.h || 32}px;
`;

// ── Availability Pill ─────────────────────────────────

function AvailPill({ status, active, onPress, saving }: {
  status: AvailabilityStatus;
  active: boolean;
  onPress: () => void;
  saving: boolean;
}) {
  const c = statusColors[status];
  return (
    <Pressable
      onPress={onPress}
      disabled={saving}
      style={({ pressed }) => ({
        flex: 1,
        paddingVertical: 10,
        borderRadius: radius.md,
        alignItems: "center" as const,
        backgroundColor: active ? c.bg : colors.bg,
        borderWidth: 1.5,
        borderColor: active ? c.border : colors.line,
        opacity: pressed ? 0.7 : saving ? 0.5 : 1,
      })}
    >
      <Text style={{ fontSize: 13, fontWeight: "700", color: active ? c.text : colors.inkMuted }}>
        {c.label}
      </Text>
    </Pressable>
  );
}

// ── Day-level Availability ────────────────────────────

function DayAvailability({ date, matches, clubId, userId, displayName, clubName, availMap }: {
  date: string;
  matches: (Match & { id: string })[];
  clubId: string;
  userId: string;
  displayName: string;
  clubName: string;
  availMap: Map<string, AvailabilityStatus>;
}) {
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const isPast = date < today;
  if (isPast) return null;

  const allOpen = matches.every((m) => isAvailabilityOpen(m.availabilityOpensAt));
  if (!allOpen) return null;

  // Compute day status from all matches
  const statuses = matches.map((m) => availMap.get(m.id) || null);
  const allSame = statuses.length > 0 && statuses.every((s) => s === statuses[0]);
  const dayStatus = allSame ? statuses[0] : null;

  const handleDayAvail = async (status: AvailabilityStatus) => {
    setSaving(true);
    try {
      await setDayAvailability(clubId, matches.map((m) => m.id), {
        userId, displayName, status, note: "", clubId, clubName, matchDate: date,
      });
      setExpanded(false);
    } catch {} finally { setSaving(false); }
  };

  if (dayStatus && !expanded) {
    return (
      <View>
        <Pressable
          onPress={() => setExpanded(true)}
          style={({ pressed }) => ({
            opacity: pressed ? 0.7 : 1,
            padding: 10,
            borderRadius: radius.md,
            backgroundColor: statusColors[dayStatus].bg,
            borderWidth: 1,
            borderColor: statusColors[dayStatus].border,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          })}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: statusColors[dayStatus].text }} />
            <Text style={{ fontSize: 13, fontWeight: "700", color: statusColors[dayStatus].text }}>
              You're {statusColors[dayStatus].label.toLowerCase()}
            </Text>
          </View>
          <Text style={{ fontSize: 12, fontWeight: "600", color: colors.inkMuted }}>Change</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={{ flexDirection: "row", gap: 8 }}>
      {(["available", "unavailable", "maybe"] as AvailabilityStatus[]).map((s) => (
        <AvailPill key={s} status={s} active={dayStatus === s} onPress={() => handleDayAvail(s)} saving={saving} />
      ))}
    </View>
  );
}

// ── Day Group (legacy, unused) ────────────────────────

function DayGroup({ date, matches, clubId, userId, displayName, clubName, clubLogoUrl }: {
  date: string;
  matches: (Match & { id: string })[];
  clubId: string;
  userId: string;
  displayName: string;
  clubName: string;
  clubLogoUrl?: string | null;
}) {
  const router = useRouter();
  const [myAvail, setMyAvail] = useState<Record<string, AvailabilityStatus | null>>({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const unsubs = matches.map((m) =>
      onSnapshot(
        doc(db, "clubs", clubId, "matches", m.id, "availability", userId),
        (snap) => {
          const status = snap.exists() ? (snap.data().status as AvailabilityStatus) : null;
          setMyAvail((prev) => ({ ...prev, [m.id]: status }));
        }
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [clubId, userId, matches.map((m) => m.id).join(",")]);

  const statuses = matches.map((m) => myAvail[m.id]);
  const allSame = statuses.length > 0 && statuses.every((s) => s === statuses[0]);
  const dayStatus = allSame ? statuses[0] : null;
  const isMulti = matches.length > 1;
  const allOpen = matches.every((m) => isAvailabilityOpen(m.availabilityOpensAt));
  const earliestOpensAt = matches
    .map((m) => m.availabilityOpensAt)
    .filter((d): d is string => !!d)
    .sort()[0];

  const handleDayAvail = async (status: AvailabilityStatus) => {
    setSaving(true);
    try {
      await setDayAvailability(clubId, matches.map((m) => m.id), {
        userId, displayName, status, note: "", clubId, clubName, matchDate: date,
      });
    } catch (err) {
      console.error("Day avail error:", err);
    } finally {
      setSaving(false);
    }
  };

  const dayLabel = formatDayHeader(date);
  const single = matches[0];

  const tint = dayStatus
    ? dayStatus === "available" ? colors.brand50
      : dayStatus === "unavailable" ? colors.dangerLight
      : colors.amber50
    : colors.white;
  const tintBorder = dayStatus
    ? dayStatus === "available" ? colors.brandLight
      : dayStatus === "unavailable" ? "#fecaca"
      : colors.amber200
    : colors.line;

  return (
    <DayCardShadow>
    <DayCard style={{ backgroundColor: tint, borderColor: tintBorder }}>
      {/* Card header — single tappable zone → match-detail (or expanded list for multi) */}
      {!isMulti ? (
        <Pressable
          onPress={() => router.push(`/match-detail?id=${single.id}`)}
          style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
        >
          <View style={{ padding: 16 }}>
            <Text style={{ fontSize: 11, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1 }}>
              {dayLabel} · {formatTime(single.time)}
            </Text>
            <View style={{ flexDirection: "row", alignItems: "center", marginTop: 10, marginBottom: 8 }}>
              <View style={{ flex: 1, gap: 4 }}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  {clubLogoUrl ? (
                    <Image source={{ uri: clubLogoUrl }} style={{ width: 24, height: 24, marginRight: 8 }} resizeMode="contain" />
                  ) : null}
                  <Text style={{ fontSize: 15, fontWeight: "800", color: colors.ink, flex: 1 }}>{single.team}</Text>
                </View>
                <Text style={{ fontSize: 11, fontWeight: "600", color: colors.inkMuted, marginLeft: clubLogoUrl ? 32 : 0 }}>vs</Text>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  {single.opponentLogoUrl ? (
                    <Image source={{ uri: single.opponentLogoUrl }} style={{ width: 24, height: 24, borderRadius: 12, marginRight: 8, backgroundColor: colors.bg }} />
                  ) : <View style={{ width: 24, height: 24, marginRight: 8 }} />}
                  <Text style={{ fontSize: 15, fontWeight: "800", color: colors.ink, flex: 1 }}>{single.opponent}</Text>
                </View>
              </View>
              <Text style={{ fontSize: 22, color: colors.inkMuted, marginLeft: 8 }}>›</Text>
            </View>
            <Text numberOfLines={1} style={{ fontSize: 12, color: colors.inkMuted }}>
              {single.venue === "home" ? "Home" : "Away"} · {single.type}{single.location ? ` · ${single.location}` : ""}
            </Text>
          </View>
        </Pressable>
      ) : (
        <View style={{ padding: 16, paddingBottom: 8 }}>
          <Text style={{ fontSize: 11, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1 }}>
            {dayLabel}
          </Text>
          <Text style={{ fontSize: 17, fontWeight: "800", color: colors.ink, marginTop: 4 }}>
            {matches.length} matches
          </Text>
          <View style={{ marginTop: 12, gap: 8 }}>
            {matches.map((m) => (
              <Pressable
                key={m.id}
                onPress={() => router.push(`/match-detail?id=${m.id}`)}
                style={({ pressed }) => ({
                  flexDirection: "row",
                  alignItems: "center",
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  borderRadius: radius.md,
                  backgroundColor: colors.bg,
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                {clubLogoUrl ? (
                  <Image source={{ uri: clubLogoUrl }} style={{ width: 22, height: 22, marginRight: 6 }} resizeMode="contain" />
                ) : null}
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, color: colors.ink }}>
                    <Text style={{ fontWeight: "700" }}>{m.team}</Text>
                    <Text style={{ fontWeight: "500", color: colors.inkMuted }}> vs </Text>
                    {m.opponentLogoUrl ? null : null}
                    <Text style={{ fontWeight: "700" }}>{m.opponent}</Text>
                  </Text>
                  <Text style={{ fontSize: 12, color: colors.inkMuted, marginTop: 2 }}>
                    {formatTime(m.time)} · {m.venue === "home" ? "Home" : "Away"}
                  </Text>
                </View>
                {m.opponentLogoUrl ? (
                  <Image source={{ uri: m.opponentLogoUrl }} style={{ width: 22, height: 22, borderRadius: 11, marginLeft: 8, backgroundColor: colors.bg }} />
                ) : null}
                <Text style={{ fontSize: 18, color: colors.inkMuted }}>›</Text>
              </Pressable>
            ))}
          </View>
        </View>
      )}

      {/* Availability pills — sole interactive zone besides the header */}
      {allOpen ? (
        dayStatus && !expanded ? (
          <Pressable
            onPress={() => setExpanded(true)}
            style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1, padding: 12, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.bg, flexDirection: "row", alignItems: "center", justifyContent: "space-between" })}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <View style={{
                width: 8, height: 8, borderRadius: 4,
                backgroundColor: statusColors[dayStatus].text,
              }} />
              <Text style={{ fontSize: 13, fontWeight: "700", color: statusColors[dayStatus].text }}>
                You're {statusColors[dayStatus].label.toLowerCase()}
              </Text>
            </View>
            <Text style={{ fontSize: 12, fontWeight: "600", color: colors.inkMuted }}>Change</Text>
          </Pressable>
        ) : (
          <View style={{ flexDirection: "row", gap: 8, padding: 12, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.bg }}>
            {(["available", "unavailable", "maybe"] as AvailabilityStatus[]).map((s) => (
              <AvailPill
                key={s}
                status={s}
                active={dayStatus === s}
                onPress={async () => { await handleDayAvail(s); setExpanded(false); }}
                saving={saving}
              />
            ))}
          </View>
        )
      ) : (
        <View style={{ padding: 14, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.bg }}>
          <Text style={{ fontSize: 12, color: colors.inkMuted, textAlign: "center", fontWeight: "600" }}>
            Availability opens {earliestOpensAt ? formatOpensAt(earliestOpensAt) : "soon"}
          </Text>
        </View>
      )}

      {/* Divergent indicator for multi-match days */}
      {isMulti && !allSame && (
        <View style={{ paddingHorizontal: 16, paddingBottom: 12, backgroundColor: colors.bg }}>
          <Text style={{ fontSize: 11, color: colors.inkMuted, fontStyle: "italic" }}>
            Different per match — open a match to set individually
          </Text>
        </View>
      )}
    </DayCard>
    </DayCardShadow>
  );
}

// ── Match Card (individual) ───────────────────────────

function MatchCard({ match, clubLogoUrl }: {
  match: Match & { id: string };
  clubLogoUrl?: string | null;
}) {
  const router = useRouter();

  const today = new Date().toISOString().slice(0, 10);
  const isPast = match.date < today;

  return (
    <DayCardShadow>
    <DayCard style={{ backgroundColor: isPast ? colors.bg : colors.white, borderColor: colors.line }}>
      <Pressable
        onPress={isPast ? undefined : () => router.push(`/match-detail?id=${match.id}`)}
        disabled={isPast}
        style={({ pressed }) => ({ opacity: isPast ? 0.6 : pressed ? 0.7 : 1 })}
      >
        <View style={{ padding: 16 }}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <View style={{ flex: 1, gap: 6 }}>
              {/* Our team */}
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                {clubLogoUrl ? (
                  <Image source={{ uri: clubLogoUrl }} style={{ width: 28, height: 28, marginRight: 10 }} resizeMode="contain" />
                ) : null}
                <Text style={{ fontSize: 16, fontWeight: "800", color: isPast ? colors.inkMuted : colors.ink, flex: 1 }}>{match.team}</Text>
              </View>
              {/* vs */}
              <Text style={{ fontSize: 12, fontWeight: "600", color: colors.inkMuted, marginLeft: clubLogoUrl ? 38 : 0 }}>vs</Text>
              {/* Opponent */}
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                {match.opponentLogoUrl ? (
                  <Image source={{ uri: match.opponentLogoUrl }} style={{ width: 28, height: 28, borderRadius: 14, marginRight: 10, backgroundColor: colors.bg }} />
                ) : <View style={{ width: 28, height: 28, marginRight: 10 }} />}
                <Text style={{ fontSize: 16, fontWeight: "800", color: isPast ? colors.inkMuted : colors.ink, flex: 1 }}>{match.opponent}</Text>
              </View>
            </View>
            {!isPast && <Text style={{ fontSize: 22, color: colors.inkMuted }}>›</Text>}
          </View>
          <Text numberOfLines={1} style={{ fontSize: 12, color: colors.inkMuted, marginTop: 10 }}>
            {formatTime(match.time)} · {match.venue === "home" ? "Home" : "Away"} · {match.type}{match.location ? ` · ${match.location}` : ""}
          </Text>
        </View>
      </Pressable>

      {isPast && (
        <View style={{ padding: 12, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.bg }}>
          <Text style={{ fontSize: 12, color: colors.inkMuted, textAlign: "center", fontWeight: "600" }}>Concluded</Text>
        </View>
      )}
    </DayCard>
    </DayCardShadow>
  );
}

// ── Training Card ─────────────────────────────────────

function TrainingCard({ training, clubId, userId, displayName, myAttendance }: {
  training: Training & { id: string };
  clubId: string;
  userId: string;
  displayName: string;
  myAttendance: "in" | "out" | null;
}) {
  const router = useRouter();
  const status = myAttendance;
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleSet = async (next: "in" | "out") => {
    setSaving(true);
    try {
      await setTrainingAttendance(clubId, training.id, {
        userId,
        displayName,
        status: next,
        trainingDate: training.date,
        trainingTitle: training.title,
        fee: training.fee,
      });
      setExpanded(false);
    } catch (err) {
      console.error("Training avail error:", err);
    } finally {
      setSaving(false);
    }
  };

  const isOpen = isAvailabilityOpen(training.availabilityOpensAt);
  const tint = status === "in" ? colors.brand50 : status === "out" ? colors.dangerLight : colors.white;
  const tintBorder = status === "in" ? colors.brandLight : status === "out" ? "#fecaca" : colors.line;

  return (
    <DayCardShadow>
      <DayCard style={{ backgroundColor: tint, borderColor: tintBorder }}>
        <Pressable
          onPress={() => router.push(`/(app)/training-detail?id=${training.id}`)}
          style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
        >
          <View style={{ padding: 16, flexDirection: "row", alignItems: "center" }}>
            <View style={{ flex: 1 }}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Text style={{ fontSize: 11, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1 }}>
                  {formatDayHeader(training.date)} · {formatTime(training.time)}
                </Text>
                <View style={{ backgroundColor: colors.amber50, borderWidth: 1, borderColor: colors.amber200, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999 }}>
                  <Text style={{ fontSize: 9, fontWeight: "800", color: colors.amber, textTransform: "uppercase", letterSpacing: 0.5 }}>Training</Text>
                </View>
              </View>
              <Text style={{ fontSize: 17, fontWeight: "800", color: colors.ink, marginTop: 2 }}>{training.title}</Text>
              <Text style={{ fontSize: 13, color: colors.inkMuted, marginTop: 2 }}>
                {training.location}
                {training.fee > 0 ? ` · £${(training.fee / 100).toFixed(2)} per session` : " · Free"}
              </Text>
            </View>
            <Text style={{ fontSize: 22, color: colors.inkMuted, marginLeft: 8 }}>›</Text>
          </View>
        </Pressable>

        {isOpen ? (
          status && !expanded ? (
            <Pressable
              onPress={() => setExpanded(true)}
              style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1, padding: 12, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.bg, flexDirection: "row", alignItems: "center", justifyContent: "space-between" })}
            >
              <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: status === "in" ? colors.brandDark : colors.danger }} />
                <Text style={{ fontSize: 13, fontWeight: "700", color: status === "in" ? colors.brandDark : colors.danger }}>
                  {status === "in" ? "You're attending" : "You're not coming"}
                </Text>
              </View>
              <Text style={{ fontSize: 12, fontWeight: "600", color: colors.inkMuted }}>Change</Text>
            </Pressable>
          ) : (
            <View style={{ flexDirection: "row", gap: 8, padding: 12, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.bg }}>
              {(["in", "out"] as const).map((s) => {
                const active = status === s;
                const c = s === "in"
                  ? { bg: colors.brand50, border: colors.brandLight, text: colors.brandDark, label: "I'm in" }
                  : { bg: colors.dangerLight, border: "#fecaca", text: colors.danger, label: "Not coming" };
                return (
                  <Pressable
                    key={s}
                    onPress={() => handleSet(s)}
                    disabled={saving}
                    style={({ pressed }) => ({
                      flex: 1,
                      paddingVertical: 10,
                      borderRadius: radius.md,
                      alignItems: "center",
                      backgroundColor: active ? c.bg : colors.bg,
                      borderWidth: 1.5,
                      borderColor: active ? c.border : colors.line,
                      opacity: pressed ? 0.7 : saving ? 0.5 : 1,
                    })}
                  >
                    <Text style={{ fontSize: 13, fontWeight: "700", color: active ? c.text : colors.inkMuted }}>
                      {c.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        )
        ) : (
          <View style={{ padding: 14, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.bg }}>
            <Text style={{ fontSize: 12, color: colors.inkMuted, textAlign: "center", fontWeight: "600" }}>
              Availability opens {training.availabilityOpensAt ? formatOpensAt(training.availabilityOpensAt) : "soon"}
            </Text>
          </View>
        )}
      </DayCard>
    </DayCardShadow>
  );
}

// ── Main Screen ───────────────────────────────────────

export default function MatchesScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { isAnyCaptain } = useTeamConfig(club?.id);
  const { matches, loading } = useMatches(club?.id);
  const { trainings } = useTrainings(club?.id);
  // availMap removed — availability is now on the Feed screen
  const attendMap = useMyTrainingAttendance(club?.id, user?.uid);
  const [segment, setSegment] = useState<"week" | "past" | "future">("week");
  const [switching, setSwitching] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState<Set<string>>(new Set());
  const [teamsExpanded, setTeamsExpanded] = useState(false);
  const allTeams = club?.teams?.length ? [...club.teams] : [...new Set(matches.map((m) => m.team))].sort();
  const hasTrainings = trainings.length > 0;
  const allFilters = hasTrainings ? [...allTeams, "Training"] : allTeams;
  const activeTeams = selectedTeams.size === 0 ? new Set(allFilters) : selectedTeams;
  const showTrainings = selectedTeams.size === 0 || selectedTeams.has("Training");

  const canCreate = membership?.role === "admin" || isAnyCaptain(user?.uid);

  // Compute window boundaries
  const today = new Date().toISOString().slice(0, 10);
  const weekEndDate = new Date();
  weekEndDate.setDate(weekEndDate.getDate() + 7);
  const weekEnd = weekEndDate.toISOString().slice(0, 10);

  const inSegment = (date: string) => {
    if (segment === "past") return date < today;
    if (segment === "week") return date >= today && date <= weekEnd;
    return date >= today; // upcoming = everything not past
  };

  // Filter matches by segment + team
  const filteredMatches = matches.filter((m) => {
    if (!inSegment(m.date)) return false;
    if (!activeTeams.has(m.team)) return false;
    return true;
  });

  // Filter trainings — only show in "This week" tab
  const visibleTrainings = trainings.filter((t) => {
    if (segment !== "week") return false;
    if (!showTrainings) return false;
    if (t.cancelled) return false;
    if (!inSegment(t.date)) return false;
    if (t.availabilityOpensAt && t.availabilityOpensAt > today) return false;
    return true;
  });

  // Flat list of items sorted by date
  type Item = { kind: "match"; date: string; match: Match & { id: string } } | { kind: "training"; date: string; training: Training & { id: string } };
  const items: Item[] = [
    ...filteredMatches.map((m) => ({ kind: "match" as const, date: m.date, match: m })),
    ...visibleTrainings.map((t) => ({ kind: "training" as const, date: t.date, training: t })),
  ].sort((a, b) => segment === "past" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date));

  // Group by date for section headers
  const sections: { date: string; items: Item[] }[] = [];
  for (const item of items) {
    const last = sections[sections.length - 1];
    if (last && last.date === item.date) { last.items.push(item); }
    else { sections.push({ date: item.date, items: [item] }); }
  }

  if (loading) return <View style={{ flex: 1, backgroundColor: colors.bg }}><MatchesSkeleton /></View>;

  return (
    <View style={{ flex: 1, backgroundColor: colors.bg }}>
      {/* Sticky header */}
      <ContentWidth>
        <Header>
          <HeaderLabel>Schedule</HeaderLabel>
          {canCreate && (
            <View style={{ flexDirection: "row", gap: 8 }}>
              <NewBtn onPress={() => router.push("/(app)/create-fixture")}>
                <NewBtnText>+ Fixture</NewBtnText>
              </NewBtn>
              <NewBtn onPress={() => router.push("/(app)/create-training")}>
                <NewBtnText>+ Training</NewBtnText>
              </NewBtn>
            </View>
          )}
        </Header>

        {/* Time segmented control */}
        <View style={{ marginHorizontal: 20, marginTop: 6, marginBottom: 10, flexDirection: "row", backgroundColor: "#f1f3f5", borderRadius: 999, padding: 3 }}>
          {(["week", "future", "past"] as const).map((s) => {
            const active = segment === s;
            const label = s === "week" ? "This week" : s === "future" ? "Upcoming" : "Past";
            return (
              <Pressable
                key={s}
                onPress={() => {
                  if (s === segment) return;
                  setSwitching(true);
                  setSegment(s);
                  setTimeout(() => setSwitching(false), 50);
                }}
                style={({ pressed }) => ({
                  flex: 1, paddingVertical: 9, alignItems: "center",
                  backgroundColor: active ? colors.brand : "transparent",
                  borderRadius: 999,
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <Text style={{ fontSize: 13, fontWeight: "700", color: active ? "#fff" : colors.inkMuted }}>{label}</Text>
              </Pressable>
            );
          })}
        </View>

        {/* Team filter — collapsible, wrapping pills */}
        {allFilters.length > 1 && (
          <View style={{ paddingHorizontal: 20, marginBottom: 14 }}>
            <Pressable
              onPress={() => {
                LayoutAnimation.configureNext(LayoutAnimation.create(200, "easeInEaseOut", "opacity"));
                setTeamsExpanded((p) => !p);
              }}
              style={({ pressed }) => ({
                flexDirection: "row", alignItems: "center", gap: 8,
                paddingVertical: 10, paddingHorizontal: 14, borderRadius: 999,
                backgroundColor: selectedTeams.size > 0 ? colors.brand50 : colors.white,
                borderWidth: 1.5, borderColor: selectedTeams.size > 0 ? colors.brandLight : colors.line,
                alignSelf: "flex-end",
                marginBottom: teamsExpanded ? 12 : 0,
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <FilterIcon size={14} color={selectedTeams.size > 0 ? colors.brandDark : colors.inkMuted} />
              <Text style={{ fontSize: 13, fontWeight: "700", color: selectedTeams.size > 0 ? colors.brandDark : colors.inkMuted }}>
                {selectedTeams.size === 0 ? "All teams" : `${selectedTeams.size} team${selectedTeams.size > 1 ? "s" : ""} selected`}
              </Text>
              {teamsExpanded ? <ChevronUpIcon size={14} color={selectedTeams.size > 0 ? colors.brandDark : colors.inkMuted} /> : <ChevronDownIcon size={14} color={selectedTeams.size > 0 ? colors.brandDark : colors.inkMuted} />}
            </Pressable>
            {teamsExpanded && (
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                <Pressable
                  onPress={() => setSelectedTeams(new Set())}
                  style={({ pressed }) => ({
                    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999,
                    backgroundColor: selectedTeams.size === 0 ? colors.brand : colors.white,
                    borderWidth: 1.5, borderColor: selectedTeams.size === 0 ? colors.brand : colors.line,
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Text style={{ fontSize: 12, fontWeight: "700", color: selectedTeams.size === 0 ? "#fff" : colors.inkMuted }}>All</Text>
                </Pressable>
                {allFilters.map((t) => {
                  const active = selectedTeams.has(t);
                  return (
                    <Pressable
                      key={t}
                      onPress={() => {
                        setSelectedTeams((prev) => {
                          if (prev.size === 0) return new Set([t]);
                          const next = new Set(prev);
                          if (next.has(t)) { next.delete(t); return next.size === 0 ? new Set() : next; }
                          next.add(t);
                          return next.size === allFilters.length ? new Set() : next;
                        });
                      }}
                      style={({ pressed }) => ({
                        paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999,
                        backgroundColor: active ? colors.brand : colors.white,
                        borderWidth: 1.5, borderColor: active ? colors.brand : colors.line,
                        opacity: pressed ? 0.7 : 1,
                      })}
                    >
                      <Text style={{ fontSize: 12, fontWeight: "700", color: active ? "#fff" : colors.inkMuted }}>{t}</Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </View>
        )}

      </ContentWidth>

      {/* Scrollable fixture list — virtualized */}
      <ContentWidth style={{ flex: 1 }}>
        {switching ? (
          <MatchesSkeleton count={3} />
        ) : sections.length === 0 ? (
          <EmptyCard>
            <Text style={{ fontSize: 36, marginBottom: 12 }}>🏏</Text>
            <EmptyText>
              {segment === "week" ? "Nothing this week." : segment === "past" ? "No past fixtures." : "Nothing scheduled."}
            </EmptyText>
          </EmptyCard>
        ) : (
          <SectionList
            sections={sections.map((s) => ({ title: s.date, data: s.items }))}
            keyExtractor={(item) => item.kind === "training" ? `t-${item.training.id}` : `m-${item.match.id}`}
            renderItem={({ item }) => {
              if (!user || !club || !membership) return null;
              if (item.kind === "training") {
                return <TrainingCard training={item.training} clubId={club.id} userId={user.uid} displayName={membership.displayName} myAttendance={attendMap.get(item.training.id) || null} />;
              }
              return <MatchCard match={item.match} clubLogoUrl={club.logoUrl} />;
            }}
            renderSectionHeader={({ section }) => (
              <Text style={{ fontSize: 12, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1, paddingHorizontal: 20, paddingTop: 16, paddingBottom: 8, backgroundColor: colors.bg }}>
                {formatDayHeader(section.title)}
              </Text>
            )}
            stickySectionHeadersEnabled={false}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingBottom: 32 }}
            initialNumToRender={10}
            maxToRenderPerBatch={10}
            windowSize={5}
          />
        )}
      </ContentWidth>
    </View>
  );
}
